/**
 * EG Exit Intent Popup
 * --------------------
 * Orchestrates a two-step email-capture popup that nudges guests to join the
 * community and earn Aura Points before they leave.
 *
 * Triggers (all gated behind an engagement threshold so drive-by traffic never
 * sees it):
 *   - Desktop: rapid cursor exit toward the top browser chrome (mouseout,
 *     relatedTarget null, clientY <= 0).
 *   - Mobile : back-button intent (history/popstate guard), a rapid scroll-up
 *     gesture, and a timed fallback.
 *
 * Engagement gate: >= N seconds on the page OR >= M% scroll depth.
 *
 * Frequency capping (localStorage + sessionStorage, try/catch wrapped):
 *   - Once per session.
 *   - 30 days (configurable) after a dismissal.
 *   - Permanently after a successful submit.
 *
 * Submission reuses Shopify's {% form 'customer' %} (subscriber + double
 * opt-in) but posts via fetch so the dialog can show Submitting / Success /
 * Error states without a full-page reload.
 */
(() => {
  if (customElements.get('eg-exit-intent-popup')) return;

  const STORAGE_KEY = 'eg_exit_intent_v1';
  const SESSION_KEY = 'eg_exit_intent_session';
  const DAY_MS = 86400000;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const mq = (query) => {
    try {
      return window.matchMedia(query).matches;
    } catch (_e) {
      return false;
    }
  };
  const isDesktopPointer = () => mq('(hover: hover) and (pointer: fine)');

  const readStore = () => {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (_e) {
      return null;
    }
  };
  const writeStore = (value) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (_e) {
      /* Safari private mode / storage disabled — non-fatal */
    }
  };
  const sessionSeen = () => {
    try {
      return window.sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (_e) {
      return false;
    }
  };
  const markSessionSeen = () => {
    try {
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch (_e) {
      /* non-fatal */
    }
  };

  class EgExitIntentPopup extends HTMLElement {
    connectedCallback() {
      if (this.__initialized) return;
      this.__initialized = true;

      this.designMode = this.dataset.designMode === 'true';
      this.sectionId = this.dataset.sectionId || '';

      this.dialogComponent = this.querySelector('dialog-component');
      this.pages = this.querySelector('[data-eip-pages]');
      this.form = this.querySelector('.eg-eip__form');
      // Custom JS validation is authoritative; suppress native constraint
      // bubbles so the empty/invalid-email and unchecked-consent cases reach
      // onSubmit and show the themed, translated error UI. The `required`
      // attributes stay as a no-JS fallback.
      if (this.form) this.form.noValidate = true;
      this.emailInput = this.querySelector('[data-eip-email]');
      this.consentInput = this.querySelector('[data-eip-consent]');
      this.errorEl = this.querySelector('[data-eip-error]');
      this.statusEl = this.querySelector('[data-eip-status]');
      this.submitBtn = this.querySelector('[data-eip-submit]');
      this.successTitle = this.querySelector('[data-eip-success-title]');

      this.strings = {
        invalidEmail: this.dataset.errEmail || 'Please enter a valid email address.',
        consent: this.dataset.errConsent || 'Please accept to continue.',
        network: this.dataset.errNetwork || 'Something went wrong — please try again.',
        generic: this.dataset.errGeneric || "We couldn't sign you up. Please try again.",
        submitting: this.dataset.submitting || 'Joining…',
      };
      this.submitLabel = this.submitBtn
        ? (this.submitBtn.querySelector('.eg-eip__submit-label')?.textContent || '').trim()
        : '';

      this.shown = false;
      this.converted = false;
      this.gateMet = false;
      this.listeners = [];
      this.timers = [];

      this.bindControls();

      // Theme editor: always preview, react to section select/deselect/unload.
      if (this.designMode) {
        this.bindEditorEvents();
        this.open();
        return;
      }

      if (this.dataset.enabled !== 'true') return;
      if (this.shouldSuppress()) return;

      this.startEngagementGate();
      this.armTriggers();
    }

    disconnectedCallback() {
      this.teardown();
    }

    /* ── Frequency capping ─────────────────────────────────────── */
    shouldSuppress() {
      if (sessionSeen()) return true;

      const store = readStore();
      if (!store) return false;
      if (store.status === 'converted') return true;
      if (store.status === 'dismissed') {
        const days = Math.max(1, parseInt(this.dataset.dismissDays, 10) || 30);
        const ts = Number(store.ts) || 0;
        if (Date.now() - ts < days * DAY_MS) return true;
      }
      return false;
    }

    /* ── Engagement gate ───────────────────────────────────────── */
    startEngagementGate() {
      const minTime = Math.max(0, parseInt(this.dataset.minTime, 10) || 0);
      const minScroll = Math.min(100, Math.max(0, parseInt(this.dataset.minScroll, 10) || 0));

      if (minTime <= 0 && minScroll <= 0) {
        this.markGate();
        return;
      }

      if (minTime > 0) {
        this.addTimer(() => this.markGate(), minTime * 1000);
      }

      if (minScroll > 0) {
        let ticking = false;
        const onScroll = () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(() => {
            ticking = false;
            const doc = document.documentElement;
            const scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
            const depth = (window.scrollY / scrollable) * 100;
            if (depth >= minScroll) this.markGate();
          });
        };
        this.addListener(window, 'scroll', onScroll, { passive: true });
      }
    }

    markGate() {
      if (this.gateMet) return;
      this.gateMet = true;

      // Mobile-only: arm the back-button history guard and timed fallback once
      // the visitor has genuinely engaged.
      if (!isDesktopPointer()) {
        if (this.dataset.mobileBack === 'true') {
          try {
            window.history.pushState({ egEip: 1 }, '');
            this.__historyPushed = true;
            this.addListener(window, 'popstate', () => {
              if (this.__skipNextPopstate) {
                this.__skipNextPopstate = false;
                return;
              }
              this.__historyConsumed = true;
              this.trigger();
            });
          } catch (_e) {
            /* history unavailable — rely on other triggers */
          }
        }

        const fallback = Math.max(0, parseInt(this.dataset.mobileFallback, 10) || 0);
        if (fallback > 0) {
          this.addTimer(() => this.trigger(), fallback * 1000);
        }
      }
    }

    /* ── Triggers ──────────────────────────────────────────────── */
    armTriggers() {
      if (isDesktopPointer()) {
        if (this.dataset.desktopExit === 'true') {
          const onMouseOut = (event) => {
            if (!this.gateMet) return;
            if (event.relatedTarget || event.toElement) return;
            if (event.clientY > 0) return;
            this.trigger();
          };
          this.addListener(document, 'mouseout', onMouseOut);
        }
        return;
      }

      // Mobile: rapid scroll-up gesture.
      if (this.dataset.mobileScrollUp === 'true') {
        let lastY = window.scrollY;
        let lastT = Date.now();
        let ticking = false;
        const onScroll = () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(() => {
            ticking = false;
            const y = window.scrollY;
            const now = Date.now();
            const dt = Math.max(16, now - lastT);
            const velocity = (lastY - y) / dt; // positive = scrolling up
            lastY = y;
            lastT = now;
            if (this.gateMet && velocity > 1.1 && y < 900) this.trigger();
          });
        };
        this.addListener(window, 'scroll', onScroll, { passive: true });
      }
    }

    trigger() {
      if (this.shown || this.converted) return;
      this.shown = true;
      markSessionSeen();
      this.teardownTriggers();
      this.open();
    }

    /* ── Dialog open / close ───────────────────────────────────── */
    open() {
      const run = () => {
        if (!this.dialogComponent || typeof this.dialogComponent.showDialog !== 'function') return;
        const dialog = this.dialogComponent.querySelector('dialog');
        if (dialog && dialog.open) return;
        this.clearError();
        this.announce('');
        this.goToStep(1, { focus: false });
        this.dialogComponent.showDialog();
        if (!this.__closeBound) {
          this.__closeBound = true;
          this.dialogComponent.addEventListener('dialog:close', () => this.onDialogClose());
        }
        // On desktop, drop focus straight into the email field so keyboard
        // users can type immediately. On touch we leave the native dialog
        // focus alone so the on-screen keyboard doesn't slam open on appear.
        if (isDesktopPointer() && this.emailInput) {
          window.requestAnimationFrame(() => {
            try {
              this.emailInput.focus({ preventScroll: true });
            } catch (_e) {
              this.emailInput.focus();
            }
          });
        }
        this.bindSwipeToClose();
      };

      if (window.customElements && typeof window.customElements.whenDefined === 'function') {
        window.customElements.whenDefined('dialog-component').then(run);
      } else {
        run();
      }
    }

    close() {
      if (this.dialogComponent && typeof this.dialogComponent.closeDialog === 'function') {
        const dialog = this.dialogComponent.querySelector('dialog');
        if (dialog && dialog.open) this.dialogComponent.closeDialog();
      }
    }

    onDialogClose() {
      this.reconcileHistory();
      if (this.designMode) return;
      // A close that isn't a conversion counts as a dismissal (30-day suppress).
      if (!this.converted) {
        writeStore({ status: 'dismissed', ts: Date.now() });
      }
    }

    // If the mobile back-button guard pushed a synthetic history entry that
    // was never consumed by a real Back press (the popup opened via scroll-up
    // or the timed fallback instead), drop it so the visitor's next Back
    // behaves normally rather than being absorbed as a no-op.
    reconcileHistory() {
      if (!this.__historyPushed || this.__historyConsumed) return;
      this.__historyConsumed = true;
      this.__skipNextPopstate = true;
      try {
        window.history.back();
      } catch (_e) {
        /* non-fatal */
      }
    }

    /* ── Step navigation ───────────────────────────────────────── */
    goToStep(step, options = {}) {
      const { focus = true } = options;
      if (!this.pages) return;
      this.pages.dataset.page = String(step);

      const allPages = this.pages.querySelectorAll('.eg-eip__page');
      allPages.forEach((page) => {
        const isActive = page.getAttribute('data-page-id') === String(step);
        page.inert = !isActive;
      });

      if (!focus) return;
      window.requestAnimationFrame(() => {
        // Step 2 is the success panel — move focus to its heading.
        if (step === 2 && this.successTitle) {
          try {
            this.successTitle.focus({ preventScroll: true });
          } catch (_e) {
            this.successTitle.focus();
          }
        }
      });
    }

    /* ── Controls ──────────────────────────────────────────────── */
    bindControls() {
      if (this.form) {
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
      }
      if (this.emailInput) {
        this.emailInput.addEventListener('input', () => this.clearError());
      }
      // On touch, ignore taps on the backdrop so an accidental tap outside the
      // sheet doesn't dismiss the popup (the close button, swipe-down and ESC
      // stay). Desktop keeps click-outside-to-close. We stop a backdrop click
      // from bubbling to dialog.js's outside-close handler.
      const dialogEl = this.dialogComponent && this.dialogComponent.querySelector('dialog');
      if (dialogEl) {
        dialogEl.addEventListener('click', (event) => {
          if (event.target === dialogEl && !isDesktopPointer()) {
            event.stopPropagation();
          }
        });
      }
      // Ensure non-active steps start inert.
      this.goToStep(1, { focus: false });
    }

    bindSwipeToClose() {
      if (this.__swipeBound || isDesktopPointer()) return;
      this.__swipeBound = true;
      const sheet = this.querySelector('.eg-eip__sheet');
      if (!sheet) return;

      let startY = 0;
      let delta = 0;
      let dragging = false;

      const start = (event) => {
        // Only a deliberate pull on the sheet chrome starts a dismiss-drag —
        // never a touch that begins on a form control, so typing / ticking
        // consent / pressing submit can't be mistaken for a swipe-to-close.
        const target = event.target;
        if (target.closest && target.closest('input, textarea, select, button, a, label')) return;
        dragging = true;
        startY = event.touches[0].clientY;
        delta = 0;
      };
      const move = (event) => {
        if (!dragging) return;
        delta = event.touches[0].clientY - startY;
      };
      const end = () => {
        if (!dragging) return;
        dragging = false;
        // Require a long, deliberate downward pull, not a small accidental drag.
        if (delta > 140) this.close();
      };

      sheet.addEventListener('touchstart', start, { passive: true });
      sheet.addEventListener('touchmove', move, { passive: true });
      sheet.addEventListener('touchend', end, { passive: true });
    }

    /* ── Submission ────────────────────────────────────────────── */
    onSubmit(event) {
      event.preventDefault();
      if (!this.form) return;
      this.clearError();

      const email = (this.emailInput && this.emailInput.value || '').trim();
      if (!EMAIL_RE.test(email)) {
        this.showError(this.strings.invalidEmail);
        if (this.emailInput) {
          this.emailInput.setAttribute('aria-invalid', 'true');
          this.emailInput.focus();
        }
        return;
      }
      if (this.consentInput && !this.consentInput.checked) {
        this.showError(this.strings.consent);
        this.consentInput.setAttribute('aria-invalid', 'true');
        this.consentInput.focus();
        return;
      }

      this.setSubmitting(true);

      const action = this.form.getAttribute('action') || '/contact';
      let body;
      try {
        body = new FormData(this.form);
      } catch (_e) {
        this.setSubmitting(false);
        this.showError(this.strings.generic);
        return;
      }

      fetch(action, {
        method: 'POST',
        body,
        redirect: 'follow',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((response) => {
          const url = response.url || '';
          // Match the theme's customer-form contract: a genuine subscribe
          // 302-redirects back with ?customer_posted=true. A bare redirect
          // (e.g. a bot/spam challenge) lacks the marker and must NOT count as
          // success, or we would falsely suppress the popup and tell the user
          // to check an inbox that never receives a confirmation email.
          const succeeded = response.ok && url.indexOf('customer_posted=true') !== -1;
          if (succeeded) {
            this.onSuccess();
          } else {
            this.setSubmitting(false);
            this.showError(this.strings.generic);
          }
        })
        .catch(() => {
          this.setSubmitting(false);
          this.showError(this.strings.network);
        });
    }

    setSubmitting(isSubmitting) {
      if (!this.submitBtn) return;
      this.submitBtn.classList.toggle('is-submitting', isSubmitting);
      this.submitBtn.disabled = isSubmitting;
      this.submitBtn.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
      const label = this.submitBtn.querySelector('.eg-eip__submit-label');
      if (label) {
        label.textContent = isSubmitting ? this.strings.submitting : this.submitLabel;
      }
      if (isSubmitting) this.announce(this.strings.submitting);
    }

    // Writes into the visually-hidden role="status" region so screen readers
    // hear the in-flight / success state (a label swap on a disabled button or
    // revealing static text is not announced on its own).
    announce(message) {
      if (this.statusEl) this.statusEl.textContent = message || '';
    }

    onSuccess() {
      this.converted = true;
      writeStore({ status: 'converted', ts: Date.now() });
      markSessionSeen();
      this.setSubmitting(false);
      this.goToStep(2);
      const title = this.successTitle ? this.successTitle.textContent.trim() : '';
      const bodyEl = this.querySelector('.eg-eip__success-body');
      const body = bodyEl ? (bodyEl.textContent || '').trim() : '';
      this.announce([title, body].filter(Boolean).join('. '));
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (this.errorEl) {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
      if (this.emailInput) this.emailInput.removeAttribute('aria-invalid');
      if (this.consentInput) this.consentInput.removeAttribute('aria-invalid');
    }

    /* ── Theme editor ──────────────────────────────────────────── */
    bindEditorEvents() {
      const matches = (event) => event && event.detail && event.detail.sectionId === this.sectionId;
      document.addEventListener('shopify:section:select', (event) => {
        if (matches(event)) this.open();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (matches(event)) this.close();
      });
      document.addEventListener('shopify:section:unload', (event) => {
        if (matches(event)) this.close();
      });
    }

    /* ── Listener bookkeeping ──────────────────────────────────── */
    addListener(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.listeners.push({ target, type, handler, options });
    }

    addTimer(callback, delay) {
      const id = window.setTimeout(callback, delay);
      this.timers.push(id);
    }

    teardownTriggers() {
      this.listeners.forEach(({ target, type, handler, options }) => {
        target.removeEventListener(type, handler, options);
      });
      this.listeners = [];
      this.timers.forEach((id) => window.clearTimeout(id));
      this.timers = [];
    }

    teardown() {
      this.teardownTriggers();
    }
  }

  customElements.define('eg-exit-intent-popup', EgExitIntentPopup);
})();
